<script setup lang="ts">
import { ref, computed } from 'vue';
import { useIcons } from 'composables/useIcons';
import { useSlots } from 'composables/useSlots';
import { ButtonProps } from '@/runtime/types/ButtonTypes';

const props = withDefaults(defineProps<ButtonProps>(), {
  color: 'neutral',
  variant: 'soft',
  size: 'md'
});

const loadingAutoState = ref(false);

const isLoading = computed(() => props.loading || (props.loadingAuto && loadingAutoState.value));

const { isLeading, isTrailing, leadingIconName, trailingIconName } = useIcons(
  computed(() => ({ ...props, loading: isLoading.value }))
);

const classes = computed(() => [
  'e-button',
  `e-button-color-${props.color}`,
  `e-button-variant-${props.variant}`,
  `e-button-size-${props.size}`,
  props.block && 'e-button-block',
  props.square && 'e-button-square',
  props.disabled && 'e-button-disabled',
  props.class
]);

const slots = useSlots();
</script>

<template>
  <component
    :is="props.as ?? 'button'"
    v-bind="{ disabled: props.disabled, target: props.target, type: props.type, href: props.to }"
    :aria-disabled="props.disabled"
    :class="classes"
  >
    <div v-if="!!slots.leading || (isLeading && leadingIconName)">
      <slot name="leading">
        <e-icon
          v-if="leadingIconName"
          :name="leadingIconName"
          size="sm"
          :class="{ 'animate-spin': isLoading }"
        />
      </slot>
    </div>

    <slot>
      {{ label }}
    </slot>

    <div v-if="!!slots.trailing || (isTrailing && trailingIconName)">
      <slot name="trailing">
        <e-icon
          v-if="trailingIconName"
          :name="trailingIconName"
          size="sm"
          :class="{ 'e-button-loading': isLoading }"
        />
      </slot>
    </div>
  </component>
</template>
